import { Component, ElementRef, forwardRef, Input, OnInit, Renderer2, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngrx/store';

@Component({
  selector: 'vb-font',
  templateUrl: './font.component.html',
  styleUrls: ['./font.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FontComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: false  //NOSONAR
})
export class FontComponent implements OnInit {
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private store = inject(Store);

  @Input() element: any;
  @Input() disableField = false;
  private modelValue: any;
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  disabled: boolean;
  formGroup: FormGroup;

  @Input() currentPage: any;
  @Input() formData: any;
  isTemplateFontGroup = false;
  fontFamilies: any[] = [
    { name: 'Arial', code: 'Arial' },
    { name: 'Verdana', code: 'Verdana' },
    { name: 'Helvetica', code: 'Helvetica' },
    { name: 'Tahoma', code: 'Tahoma' },
    { name: 'Trebuchet MS', code: 'Trebuchet MS' },
    { name: 'Times New Roman', code: 'Times New Roman' },
    { name: 'Georgia', code: 'Georgia' },
    { name: 'Garamond', code: 'Garamond' },
    { name: 'Courier New', code: 'Courier New' },
    { name: 'Brush Script MT', code: 'Brush Script MT' }
  ];

  fontWeights: any[] = [
    { name: 'normal', code: 'normal' },
    { name: 'bold', code: 'bold' },
    { name: 'lighter', code: 'lighter' },
    { name: 'bolder', code: 'bolder' },
    { name: '100', code: '100' },
    { name: '200', code: '200' },
    { name: '300', code: '300' },
    { name: '400', code: '400' },
    { name: '500', code: '500' },
    { name: '600', code: '600' },
    { name: '700', code: '700' },
    { name: '800', code: '800' },
    { name: '900', code: '900' }
  ];

  fontStyles: any[] = [
    { name: 'normal', code: 'normal' },
    { name: 'italic', code: 'italic' },
    { name: 'oblique', code: 'oblique' }
  ];

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.setSelectedFonts(value);
    this.modelValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set(value: any) {
    this.modelValue = value;
    this.onChange(this.modelValue);
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    this.disabled = isDisabled;
  }

  setSelectedFonts(value: any) {
    if (value) {
      this.formGroup.patchValue(value);
    } else {
      this.formGroup.reset();
    }
  }

  handleFontChanges(e: any, op: any) {
    op.hide(e);
    this.modelValue = this.formGroup.value;
    this.onChange(this.modelValue);
  }

  setForm(
    fontFamily: any = 'Arial',
    fontSize: any = '12px',
    fontWeight: any = 'normal',
    fontStyle: any = 'normal',
    fontColor: any = '#333333'
  ) {
    const filteredFontFamilyArray = this.fontFamilies.filter(
      (fontFamilyObj) => fontFamily?.toLowerCase()?.indexOf(fontFamilyObj?.name?.toLowerCase()) >= 0
    );
    const fontFamilyObj = filteredFontFamilyArray?.length === 0 ? this.fontFamilies[0] : filteredFontFamilyArray[0];
    this.formGroup = new FormGroup({
      fontFamily: new FormControl(fontFamilyObj),
      fontSize: new FormControl(fontSize),
      fontWeight: new FormControl({ name: fontWeight, code: fontWeight }),
      fontStyle: new FormControl({ name: fontStyle, code: fontStyle }),
      fontColor: new FormControl(fontColor)
    });
  }

  getStyles() {
    return {
      fontFamily: this.formGroup.value.fontFamily?.code,
      fontSize: this.formGroup.value.fontSize,
      fontWeight: this.formGroup.value.fontWeight?.code,
      fontStyle: this.formGroup.value.fontStyle?.code,
      color: this.formGroup.value.fontColor
    };
  }

  ngOnInit(): void {
    if (this.currentPage?.currentPage === 'templates') {
      this.fontFamilies = [
        { name: 'Poppins', code: 'Poppins' },
        { name: 'Roboto', code: 'Roboto' },
        { name: 'Open Sans', code: 'Open Sans' },
        { name: 'Lato', code: 'Lato' },
        { name: 'Montserrat', code: 'Montserrat' },
        { name: 'Oswald', code: 'Oswald' },
        { name: 'Source Sans Pro', code: 'Source Sans Pro' },
        { name: 'PT Sans', code: 'PT Sans' },
        { name: 'Merriweather', code: 'Merriweather' },
        { name: 'Noto Sans', code: 'Noto Sans' },
        { name: 'Nunito Sans', code: 'Nunito Sans' }
      ];
      const propertyName = this.element?.name?.substring(0, this.element?.name?.length - 6);
      this.triggerFormChanges(propertyName, this.formData);
      this.isTemplateFontGroup = true;
    } else {
      this.setForm();
    }
  }

  triggerFormChanges(propertyName: any = '', currFormData: any = '') {
    if (propertyName) {
      this.setForm(
        currFormData[`${propertyName}family`],
        currFormData[`${propertyName}size`],
        currFormData[`${propertyName}weight`],
        currFormData[`${propertyName}style`],
        currFormData[`${propertyName}color`]
      );
    }
  }
}
